import { isDefined } from "~/shared/common";
import { IconKind, TaskKindKind } from "~/shared/enums";
import type { TaskKind, TaskKindUpdate } from "~/shared/types";
import { makeRandomColorHex } from "~/utils/color";
import { makeDuid, randomSample } from "~/utils/common";
import { taskKindComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, setObjectMapAndSorted, updateInSorted } from "./utils";

export type State = {
  _duidsToTaskKinds: Map<string, TaskKind>;
  _sortedTaskKindDuids: string[];
};

export const state: State = {
  _duidsToTaskKinds: new Map(),
  _sortedTaskKindDuids: [],
};

export type Getters = {
  taskKindList: TaskKind[];
  getTaskKindByDuid: (duid: string) => TaskKind | undefined;
  getTaskKindByTitle: (title: string) => TaskKind | undefined;
  getTaskKindsByDuidsOrdered: (duids: string[]) => TaskKind[];
  defaultTaskKind: TaskKind;
};

export type Actions = {
  createTaskKind: (
    title: string,
    order: string,
    partialTaskKind?: Partial<TaskKind>,
    options?: { awaitBackend?: boolean }
  ) => Promise<TaskKind | undefined>;
  updateTaskKind: (
    update: TaskKindUpdate,
    options?: { awaitBackend?: boolean; noBackend?: boolean }
  ) => Promise<TaskKind | undefined>;
  updateTaskKinds: (
    updates: TaskKindUpdate[],
    options?: { awaitBackend?: boolean; noBackend?: boolean }
  ) => Promise<(TaskKind | undefined)[]>;
  deleteTaskKind: (taskKind: TaskKind, options?: { awaitBackend?: boolean }) => Promise<void>;
  $createTaskKindNoBackend: (title: string, order: string, partialTaskKind?: Partial<TaskKind>) => TaskKind | undefined;
  $setTaskKinds: (taskKinds: TaskKind[]) => void;
  $createOrUpdateTaskKind: (taskKind: TaskKind) => void;
  $deleteTaskKind: (taskKind: TaskKind) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  taskKindList() {
    return this._sortedTaskKindDuids.map((e) => this._duidsToTaskKinds.get(e)).filter(isDefined);
  },
  getTaskKindByDuid() {
    return (duid) => this._duidsToTaskKinds.get(duid);
  },
  getTaskKindByTitle() {
    return (title) => {
      const normTitle = title.trim().toLowerCase();
      return this.taskKindList.find((e: TaskKind) => e.title.toLowerCase() === normTitle);
    };
  },
  getTaskKindsByDuidsOrdered() {
    return (duids) => this.taskKindList.filter((e) => duids.includes(e.duid));
  },
  defaultTaskKind() {
    const taskKind = this.taskKindList.filter((e) => e.locked);
    if (!taskKind) {
      throw new Error("Expected exactly one locked task kind");
    }
    return taskKind[0];
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async createTaskKind(title, order, partialTaskKind = {}, options = {}) {
    const { awaitBackend = false } = options;
    const taskKind = this.$createTaskKindNoBackend(title, order, partialTaskKind);
    if (!taskKind) {
      return undefined;
    }

    const backendAction = this.$backend.taskKind.create(taskKind);
    if (awaitBackend) {
      await backendAction;
    }
    return taskKind;
  },
  async updateTaskKind(update, options = {}) {
    const taskKinds = await this.updateTaskKinds([update], options);
    return taskKinds[0];
  },
  async updateTaskKinds(updates, options = {}) {
    const { awaitBackend = false, noBackend = false } = options;
    const taskKinds: TaskKind[] = [];
    updates.forEach((update) => {
      const taskKind = this.getTaskKindByDuid(update.duid);
      if (!taskKind) {
        return;
      }

      taskKinds.push(taskKind);
      Object.assign(taskKind, update);
      updateInSorted(this._duidsToTaskKinds, this._sortedTaskKindDuids, update.duid, taskKindComparator);
    });

    if (taskKinds.length === 0) {
      return [];
    }

    if (!noBackend) {
      const backendAction = this.$backend.taskKind.updateMany(updates);
      if (awaitBackend) {
        await backendAction;
      }
    }
    return taskKinds;
  },
  async deleteTaskKind(taskKind, options = {}) {
    const { awaitBackend = false } = options;

    const tasks = this.getAllLoadedTasksForUiOnly({ includeTrashed: true, includeDraft: true }).filter(
      (e) => e.kindDuid === taskKind.duid
    );
    const newTaskKindDuid = this.defaultTaskKind.duid;
    if (!newTaskKindDuid) {
      return;
    }
    this.updateTasks(
      tasks.map((e) => ({
        duid: e.duid,
        kindDuid: newTaskKindDuid,
      })),
      { noUndo: true, noCelebrate: true, noBackend: true }
    );

    this.$cleanupFormFieldsDefaultsNoBackend([taskKind.duid], [this.defaultKindProperty.duid]);
    this.$cleanupLayoutsFiltersNoBackend([taskKind.duid], [this.defaultKindProperty.duid]);

    this.$deleteTaskKind(taskKind);

    const backendAction = this.$backend.taskKind.delete(taskKind.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $createTaskKindNoBackend(title, order, partialTaskKind = {}) {
    if (title !== "" && this.getTaskKindByTitle(title) !== undefined) {
      return undefined;
    }

    const appStore = this.$useAppStore();

    const taskKind: TaskKind = {
      duid: makeDuid(),
      kind: TaskKindKind.DEFAULT,
      locked: false,
      order,
      title: title.trim(),
      iconKind: IconKind.NONE,
      iconNameOrEmoji: randomSample(appStore.pageIconOptions)[0].name,
      colorHex: makeRandomColorHex(),
      hiddenStatusDuids: [],
      ...partialTaskKind,
    };

    this.$createOrUpdateTaskKind(taskKind);

    return taskKind;
  },
  $setTaskKinds(taskKinds) {
    setObjectMapAndSorted(this._duidsToTaskKinds, this._sortedTaskKindDuids, taskKinds, taskKindComparator);
  },
  $createOrUpdateTaskKind(taskKind) {
    const currentTaskKind = this.getTaskKindByDuid(taskKind.duid);

    if (!currentTaskKind) {
      this._duidsToTaskKinds.set(taskKind.duid, taskKind);
    } else {
      Object.assign(currentTaskKind, taskKind);
    }
    updateInSorted(this._duidsToTaskKinds, this._sortedTaskKindDuids, taskKind.duid, taskKindComparator);
  },
  $deleteTaskKind(taskKind) {
    this._duidsToTaskKinds.delete(taskKind.duid);
    removeFromSorted(this._sortedTaskKindDuids, taskKind.duid);
  },
};

export { actions, getters };
